import { centsToDollars } from "@outschool/business-rules";
import Cookies from "js-cookie";
import React from "react";

import pkg from "../../lib/pkg";
import { AnalyticsError } from "../../lib/util";
import { IntegrationCategory } from "../../providers/AnalyticsContext";
import useTrackEvent from "../useTrackEvent";
import { mapActivityListingToProduct, mapGiftCardToProduct } from "./util";

import type {
  ActivityListing,
  Enrollment,
  GiftCard,
  Payment,
  Products,
  Section,
  User,
} from "./types";

const EVENT_NAME = "Order Completed";

/*
 * Note: `checkout_id` is set within the EnrichmentPlugin since it's a
 * common event property referenced throughout the checkout flow.
 */
type StandardOrderCompletedProperties = {
  checkout_id?: string;
  order_id: string;
  affiliation: string;
  subtotal?: number;
  // Note GA Ecommerce destination accepts total or revenue, but not both
  total: number;
  revenue?: number;
  shipping?: number;
  tax?: number;
  discount?: number;
  coupon?: string;
  currency: string;
  products: Products;
};

type CustomOrderCompletedProperties = {
  section_uid?: string;
  activity_uid?: string;
  is_auto_schedule_enrollment?: boolean;
  is_enrollment_in_unpublished_section?: boolean;
  category?: string | null;
  campaignId?: string;
  templateId?: string;
  is_first_purchase?: boolean;
  email?: string;
  enrollment_duration?: string;
};

type OrderCompletedProperties = StandardOrderCompletedProperties &
  CustomOrderCompletedProperties;

interface TrackOrderCompletedProperties {
  activityListing?: ActivityListing;
  enrollments?: Array<Enrollment>;
  enrollmentDuration?: string;
  giftCard?: GiftCard;
  section?: Section;
  user?: User;
}

export default function useOrderCompleted() {
  const trackAdEvent = useTrackEvent(IntegrationCategory.Advertising);
  const [trackedOrderId, setTrackedOrderId] = React.useState<string>("");

  function track({
    activityListing,
    enrollments,
    enrollmentDuration,
    giftCard,
    section,
    user,
  }: TrackOrderCompletedProperties) {
    try {
      let properties: OrderCompletedProperties | undefined = undefined;
      if (!!activityListing && !!enrollments && !!user) {
        properties = mapEnrollmentsToOrderCompleted({
          listing: activityListing,
          enrollments,
          enrollmentDuration,
          section,
          user,
        });
      } else if (!!giftCard && !!user) {
        properties = mapGiftCardToOrderCompleted(giftCard, user);
      }

      if (!properties || properties.order_id === trackedOrderId) {
        return;
      }

      trackAdEvent(EVENT_NAME, properties);
      setTrackedOrderId(properties.order_id);
    } catch (error) {
      pkg.onError(new AnalyticsError(EVENT_NAME, error));
    }
  }

  return React.useCallback(track, [trackAdEvent, trackedOrderId]);
}

/*
 * `StandardOrderCompletedProperties` should always take precedence over our
 * `CustomOrderCompletedProperties` to avoid conflicting with multiple event
 * destinations downstream.
 */
function mapEnrollmentsToOrderCompleted({
  listing,
  enrollments,
  enrollmentDuration,
  section,
  user,
}: {
  listing: ActivityListing;
  enrollments: Array<Enrollment>;
  enrollmentDuration?: string;
  section?: Section;
  user: User;
}): OrderCompletedProperties {
  const customProperties = mapToCustomOrderCompletedProperties({
    listing,
    enrollments,
    enrollmentDuration,
    section,
    user,
  });
  const standardProperties = mapToStandardOrderCompletedProperties(
    listing,
    enrollments,
  );

  return {
    ...customProperties,
    ...standardProperties,
  };
}

export function mapGiftCardToOrderCompleted(
  giftCard: GiftCard,
  user: User,
): OrderCompletedProperties {
  const total = centsToDollars(giftCard.amountCents);
  const product = mapGiftCardToProduct(giftCard);

  return {
    affiliation: "Outschool",
    currency: giftCard.currencyCode,
    email: user?.email,
    order_id: giftCard?.orderSlugId || "",
    products: [product],
    revenue: total,
    total,
  };
}

export function mapToStandardOrderCompletedProperties(
  listing: ActivityListing,
  enrollments: Array<Enrollment>,
): StandardOrderCompletedProperties {
  const product = mapActivityListingToProduct(listing);
  const couponPayments = findPaymentsWithCoupon(enrollments);
  const total = getTotalDollarsFromEnrollments(enrollments);

  let discount: number | undefined;
  let coupon: string | undefined;
  if (!!couponPayments?.length) {
    discount = getDiscountFromCouponPayments(couponPayments);
    coupon = couponPayments?.[0]?.couponCode?.code;
    product.coupon = couponPayments?.[0]?.couponCode?.code;
  }

  product.quantity = enrollments.length;

  return {
    affiliation: "Outschool",
    coupon,
    currency: getCurrencyCodeFromEnrollments(enrollments),
    discount,
    order_id: getOrderSlugIdFromEnrollments(enrollments),
    products: [product],
    revenue: total,
    total,
  };
}

/*
 * Note: `email` is included for Google Enhanced Conversions.
 */
export function mapToCustomOrderCompletedProperties({
  listing,
  enrollments,
  enrollmentDuration,
  section,
  user,
}: {
  listing: ActivityListing;
  enrollments: Array<Enrollment>;
  enrollmentDuration?: string;
  section?: Section;
  user: User;
}): CustomOrderCompletedProperties {
  let is_enrollment_in_unpublished_section: boolean | undefined;

  if (!!section && section?.published_at === undefined) {
    is_enrollment_in_unpublished_section = undefined;
  } else {
    is_enrollment_in_unpublished_section = !section?.published_at;
  }

  return {
    activity_uid: listing.activity.uid,
    campaignId: Cookies.get("iterableEmailCampaignId"),
    category: listing.activity.subject,
    email: user.email,
    // eslint-disable-next-line no-restricted-globals
    is_auto_schedule_enrollment: location?.search?.includes("isAutoScheduled"),
    is_enrollment_in_unpublished_section,
    is_first_purchase: isFirstPurchase(enrollments),
    section_uid: section?.uid,
    templateId: Cookies.get("iterableTemplateId"),
    enrollment_duration: enrollmentDuration,
  };
}

export function getCurrencyCodeFromEnrollments(
  enrollments: Array<Enrollment>,
): string {
  return enrollments?.[0]?.payments?.[0]?.currency_code;
}

export function getOrderSlugIdFromEnrollments(
  enrollments: Array<Enrollment>,
): string {
  return enrollments?.[0]?.payments?.[0]?.order_slug_id;
}

export function getTotalDollarsFromEnrollments(
  enrollments: Array<Enrollment>,
): number {
  if (!enrollments) {
    return 0;
  }

  let totalCents = 0;
  enrollments.forEach(enrollment => {
    totalCents += enrollment.price_cents;
  });

  return centsToDollars(totalCents);
}

export function isFirstPurchase(enrollments: Array<Enrollment>): boolean {
  return enrollments.some(enrollment => enrollment.isFirstPurchaseByUser);
}

export function findPaymentsWithCoupon(
  enrollments: Array<Enrollment>,
): Array<Payment> {
  const payments: Array<Payment> = [];

  enrollments.forEach(enrollment => {
    if (!enrollment.payments) {
      return;
    }

    payments.push(...enrollment.payments);
  });

  return payments.filter(payment => !!payment?.couponCode?.code);
}

export function getDiscountFromCouponPayments(
  payments: Array<Payment>,
): number | undefined {
  if (!payments.length) {
    return undefined;
  }

  let discount = 0;
  payments.forEach(payment => {
    discount += payment.amount_cents;
  });

  return centsToDollars(discount);
}
